import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  sumMoney
} from '@/components/WorkbookPage';
import { Calendar, CheckCircle2 } from 'lucide-react';

export const Rule3Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule3 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule3', { ...data, [field]: value });
  };
  
  // Simple bill tracker state
  const [bills10th, setBills10th] = useState(data.bills10th || [
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
  ]);
  
  const [bills25th, setBills25th] = useState(data.bills25th || [
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
    { name: '', amount: '', paid: false },
  ]);
  
  const updateBill = (list, setList, index, field, value) => {
    const newList = [...list];
    newList[index] = { ...newList[index], [field]: value };
    setList(newList);
    const is10 = list === bills10th;
    updateFormData('rule3', {
      ...data,
      [is10 ? 'bills10th' : 'bills25th']: newList,
      [is10 ? 'total10th' : 'total25th']: sumMoney(newList, 'amount').toFixed(2)
    });
  };

  const total10th = sumMoney(bills10th, 'amount');
  const total25th = sumMoney(bills25th, 'amount');
  
  return (
    <WorkbookPage pageId="rule-3" prevPage="rule-2" nextPage="rule-4">
      <RuleHeader ruleNumber="3" title="The 10th & 25th Planning System" section="stability" />
      
      {/* Overview */}
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Most bills are due around two times per month: around the <strong>10th</strong> and around 
        the <strong>25th</strong>. By organizing your bills into these two groups and planning 
        your payments around these dates, you create a simple, repeatable system.
      </Paragraph>
      <Paragraph>
        This works no matter how often you get paid—weekly, biweekly, or monthly. The key is 
        knowing which bills come first and making sure the money is there before they're due.
      </Paragraph>
      
      {/* Why It Matters */}
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        Late fees add up fast. A $35 late fee here, a $25 penalty there—before you know it, 
        you've lost $100 or more that could have gone toward your goals. The 10th & 25th 
        system keeps you ahead of due dates.
      </Paragraph>
      
      <Callout type="warning" title="Important">
        If a bill is due on the 10th, you need the money in your Bills Account by the 9th at the latest. 
        Plan for money to arrive 1-2 days early to avoid surprises.
      </Callout>
      
      {/* Visual Calendar Concept */}
      <div className="my-8 bg-secondary/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="font-serif font-bold">How It Works: A Monthly View</h3>
        </div>
        
        <div className="grid grid-cols-7 gap-1 text-center text-xs mb-4">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className="font-semibold text-muted-foreground py-2">{day}</div>
          ))}
          {/* Simple calendar visualization */}
          {Array.from({ length: 35 }, (_, i) => {
            const day = i - 3; // Offset to start month on Thursday
            const isValid = day >= 1 && day <= 31;
            const is10th = day === 10;
            const is25th = day === 25;
            return (
              <div 
                key={i} 
                className={`py-2 rounded ${
                  is10th ? 'bg-destructive/20 font-bold text-destructive' :
                  is25th ? 'bg-info/20 font-bold text-info' :
                  isValid ? 'text-muted-foreground' : ''
                }`}
              >
                {isValid ? day : ''}
              </div>
            );
          })}
        </div>
        
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-destructive/20" />
            <span>10th - First batch of bills</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-info/20" />
            <span>25th - Second batch of bills</span>
          </div>
        </div>
      </div>
      
      {/* Examples for different pay schedules */}
      <SectionTitle>Examples by Pay Schedule</SectionTitle>
      
      <ExampleBox title="Weekly Pay">
        <p><strong>Week 1 (1st-7th):</strong> Set aside money for 10th bills</p>
        <p><strong>Week 2 (8th-14th):</strong> Pay 10th bills, start saving for 25th</p>
        <p><strong>Week 3 (15th-21st):</strong> Continue building for 25th bills</p>
        <p><strong>Week 4 (22nd-28th):</strong> Pay 25th bills</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Tip: Each paycheck, transfer the right portion to your Bills Account immediately.
        </p>
      </ExampleBox>
      
      <ExampleBox title="Biweekly Pay (1st & 15th)">
        <p><strong>Paycheck on 1st:</strong> Cover all bills due around 10th + half of 25th bills</p>
        <p><strong>Paycheck on 15th:</strong> Cover remaining 25th bills + start building for next 10th</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Tip: Know exactly which bills each paycheck covers.
        </p>
      </ExampleBox>
      
      <ExampleBox title="Monthly Pay (Paid on 1st)">
        <p><strong>On payday:</strong> Immediately transfer full Bills Account amount</p>
        <p><strong>By 9th:</strong> Pay/schedule all 10th bills</p>
        <p><strong>By 24th:</strong> Pay/schedule all 25th bills</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Tip: If you're paid once a month, all bill money moves at once. Don't let it sit in Spending.
        </p>
      </ExampleBox>
      
      <ExampleBox title="Irregular Income">
        <p><strong>Strategy:</strong> Build a one-month buffer so you're paying THIS month's bills with 
        LAST month's income. Until then, pay bills in order of importance:</p>
        <ol className="list-decimal list-inside mt-2 text-sm space-y-1">
          <li>Housing (keeps roof over your head)</li>
          <li>Utilities (keeps lights on)</li>
          <li>Transportation (keeps you getting to work)</li>
          <li>Food (keeps you healthy)</li>
          <li>Everything else</li>
        </ol>
      </ExampleBox>
      
      {/* Step by Step */}
      <SectionTitle>Step-by-Step: Setting Up Your 10th/25th System</SectionTitle>
      <StepList steps={[
        "List ALL your monthly bills with their due dates.",
        "Sort them into two groups: Due by the 10th (1st-15th) and Due by the 25th (16th-31st).",
        "Add up each group to know how much you need by each date.",
        "Look at your pay schedule: Which paychecks cover which bills?",
        "Set reminders for 2-3 days before the 10th and 25th to review and pay bills.",
        "Consider setting up auto-pay for bills with consistent amounts (like phone, subscriptions).",
        "Always leave a small buffer ($20-50) in your Bills Account for timing variations."
      ]} />
      
      {/* Worksheet */}
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your 10th/25th Planning Worksheet</h3>
        
        <div className="space-y-8">
          {/* Bills due by 10th */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wide text-destructive mb-4">
              Bills Due Around the 10th (1st-15th)
            </h4>
            <div className="space-y-3">
              {bills10th.map((bill, index) => (
                <div key={index} className="flex items-center gap-3">
                  <button
                    onClick={() => updateBill(bills10th, setBills10th, index, 'paid', !bill.paid)}
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      bill.paid ? 'bg-success border-success' : 'border-border'
                    }`}
                  >
                    {bill.paid && <CheckCircle2 className="h-4 w-4 text-white" />}
                  </button>
                  <input
                    type="text"
                    className="flex-1 border-b border-dashed border-border bg-transparent py-2 focus:outline-none focus:border-primary"
                    placeholder="Bill name"
                    value={bill.name}
                    onChange={(e) => updateBill(bills10th, setBills10th, index, 'name', e.target.value)}
                  />
                  <input
                    type="text"
                    className="w-24 border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none focus:border-primary"
                    placeholder="$___"
                    value={bill.amount}
                    onChange={(e) => updateBill(bills10th, setBills10th, index, 'amount', e.target.value)}
                  />
                </div>
              ))}
            </div>
            <AutoTotal
              label="Total due by 10th:"
              value={total10th}
              hint="Adds up automatically from the bills above"
              testId="rule3-total-10th"
            />
          </div>
          
          {/* Bills due by 25th */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wide text-info mb-4">
              Bills Due Around the 25th (16th-31st)
            </h4>
            <div className="space-y-3">
              {bills25th.map((bill, index) => (
                <div key={index} className="flex items-center gap-3">
                  <button
                    onClick={() => updateBill(bills25th, setBills25th, index, 'paid', !bill.paid)}
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      bill.paid ? 'bg-success border-success' : 'border-border'
                    }`}
                  >
                    {bill.paid && <CheckCircle2 className="h-4 w-4 text-white" />}
                  </button>
                  <input
                    type="text"
                    className="flex-1 border-b border-dashed border-border bg-transparent py-2 focus:outline-none focus:border-primary"
                    placeholder="Bill name"
                    value={bill.name}
                    onChange={(e) => updateBill(bills25th, setBills25th, index, 'name', e.target.value)}
                  />
                  <input
                    type="text"
                    className="w-24 border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none focus:border-primary"
                    placeholder="$___"
                    value={bill.amount}
                    onChange={(e) => updateBill(bills25th, setBills25th, index, 'amount', e.target.value)}
                  />
                </div>
              ))}
            </div>
            <AutoTotal
              label="Total due by 25th:"
              value={total25th}
              hint="Adds up automatically from the bills above"
              testId="rule3-total-25th"
            />
          </div>
          
          <div className="bg-secondary rounded-lg p-4">
            <WorksheetInput
              label="Your pay dates each month:"
              placeholder="e.g., Every Friday, or 1st and 15th, or 1st of month only"
              value={data.payDates || ''}
              onChange={(v) => updateData('payDates', v)}
            />
          </div>
          
          <WorksheetInput
            label="Notes on your payment plan:"
            placeholder="Which paychecks cover which bills? Any adjustments needed?"
            value={data.planningNotes || ''}
            onChange={(v) => updateData('planningNotes', v)}
            multiline
          />
        </div>
      </div>
      
      {/* Checklist */}
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem 
          checked={data.checklist1 || false}
          onChange={(v) => updateData('checklist1', v)}
        >
          I've sorted all my bills into 10th and 25th groups
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist2 || false}
          onChange={(v) => updateData('checklist2', v)}
        >
          I know the total needed by each date
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist3 || false}
          onChange={(v) => updateData('checklist3', v)}
        >
          I've matched my pay schedule to my bill due dates
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist4 || false}
          onChange={(v) => updateData('checklist4', v)}
        >
          I've set up reminders or calendar alerts
        </CheckboxItem>
      </div>
      
      {/* Recap */}
      <RecapBox items={[
        "Group bills into two batches: 10th and 25th",
        "Know the total needed by each date",
        "Match your paychecks to your bill schedule",
        "Pay 1-2 days early to avoid surprises",
        "Consider auto-pay for consistent bills"
      ]} />
      
      {/* Affirmation */}
      <AffirmationBox>
        I stay ahead of my bills, not behind them. Planning ahead gives me peace and control.
      </AffirmationBox>
      
      {/* Optional Reflection */}
      <OptionalSection>
        <p className="mb-4">Reflect on these questions:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>Have late fees cost you money in the past? How much might you save by avoiding them?</li>
          <li>What does it feel like to imagine having bills paid before they're due?</li>
          <li>Is there a trusted person who could be your "accountability partner" for bill payment?</li>
        </ul>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
